import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, 
  Building2, 
  Home,
  Download,
  Calendar,
  DollarSign,
  PieChart,
  BarChart3,
  RefreshCw,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { agencyBalanceAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const monthNames = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const AgencyBalance = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [balanceData, setBalanceData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Generate year options (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await agencyBalanceAPI.getBalance(selectedYear);
      setBalanceData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du bilan');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await agencyBalanceAPI.downloadPdf(selectedYear);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bilan_agence_${selectedYear}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Bilan PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1B3A4B]" />
      </div>
    );
  }

  const summary = balanceData?.summary || {};
  const monthlyBreakdown = balanceData?.monthly_breakdown || [];
  const rentalByProperty = balanceData?.rental_by_property || [];

  // Calculate percentages
  const salesPercentage = summary.total_commission > 0 
    ? ((summary.total_sales_commission / summary.total_commission) * 100).toFixed(1)
    : 0;
  const rentalPercentage = summary.total_commission > 0 
    ? ((summary.total_rental_commission / summary.total_commission) * 100).toFixed(1)
    : 0;

  // Find max commission for chart scaling
  const maxMonthlyCommission = Math.max(...monthlyBreakdown.map(m => m.total_commission), 1);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="agency-balance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-[#1B3A4B]" />
            Bilan de l'Agence
          </h1>
          <p className="text-gray-500 mt-1">
            Suivi des commissions sur ventes et locations
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[140px]" data-testid="year-select">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isAdmin && (
          <Button 
            onClick={handleDownloadPdf}
            className="bg-[#1B3A4B] hover:bg-[#142D3A]"
            disabled={downloading}
            data-testid="download-pdf-btn"
          >
            {downloading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Telecharger PDF
          </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className={`grid grid-cols-1 gap-4 ${isAdmin ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2'}`}>
        {/* Total Commission - Admin only */}
        {isAdmin && (
          <Card className="border-l-4 border-l-[#1B3A4B]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Commissions</p>
                  <p className="text-2xl font-bold text-[#1B3A4B]" data-testid="total-commission">
                    {formatCurrency(summary.total_commission || 0, 'XOF')}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#1B3A4B]/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-[#1B3A4B]" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sales Commission - Admin only */}
        {isAdmin && (
          <Card className="border-l-4 border-l-[#D4A373]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Commissions Ventes</p>
                  <p className="text-2xl font-bold text-[#D4A373]">
                    {formatCurrency(summary.total_sales_commission || 0, 'XOF')}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {salesPercentage}% du total &bull; {summary.sales_count || 0} vente(s)
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#D4A373]/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-[#D4A373]" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rental Commission - Admin only */}
        {isAdmin && (
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Commissions Locations</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(summary.total_rental_commission || 0, 'XOF')}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {rentalPercentage}% du total &bull; {summary.rental_payments_count || 0} paiement(s)
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Home className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Properties with Rentals - visible to all */}
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Biens en Location</p>
                <p className="text-2xl font-bold text-purple-600">
                  {summary.properties_with_rentals || 0}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Loyers: {formatCurrency(summary.total_rent_collected || 0, 'XOF')}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                <PieChart className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Rent - visible to all (non-commission info) */}
        {!isAdmin && (
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Loyers Encaisses</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(summary.total_rent_collected || 0, 'XOF')}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {summary.rental_payments_count || 0} paiement(s)
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Charts Section - Admin only */}
      {isAdmin && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="w-5 h-5 text-[#1B3A4B]" />
              Évolution Mensuelle
            </CardTitle>
            <CardDescription>Commissions par mois en {selectedYear}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {monthlyBreakdown.map((month) => {
                const percentage = (month.total_commission / maxMonthlyCommission) * 100;
                const salesWidth = month.total_commission > 0 
                  ? (month.sales_commission / month.total_commission) * 100 
                  : 0;
                
                return (
                  <div key={month.month} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-gray-700">{monthNames[month.month]}</span>
                      <span className="text-gray-500">
                        {formatCurrency(month.total_commission, 'XOF')}
                      </span>
                    </div>
                    <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full flex rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      >
                        <div 
                          className="h-full bg-[#D4A373]"
                          style={{ width: `${salesWidth}%` }}
                          title={`Ventes: ${formatCurrency(month.sales_commission, 'XOF')}`}
                        />
                        <div 
                          className="h-full bg-blue-500"
                          style={{ width: `${100 - salesWidth}%` }}
                          title={`Locations: ${formatCurrency(month.rental_commission, 'XOF')}`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#D4A373]" />
                <span className="text-sm text-gray-600">Ventes</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm text-gray-600">Locations</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Distribution Pie (simplified as stats) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <PieChart className="w-5 h-5 text-[#1B3A4B]" />
              Répartition des Commissions
            </CardTitle>
            <CardDescription>Distribution entre ventes et locations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              {/* Visual pie representation */}
              <div className="relative w-48 h-48">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  {/* Sales slice */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="transparent"
                    stroke="#D4A373"
                    strokeWidth="20"
                    strokeDasharray={`${salesPercentage * 2.51} 251`}
                  />
                  {/* Rental slice */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="transparent"
                    stroke="#3B82F6"
                    strokeWidth="20"
                    strokeDasharray={`${rentalPercentage * 2.51} 251`}
                    strokeDashoffset={`${-salesPercentage * 2.51}`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-[#1B3A4B]">
                      {formatCurrency(summary.total_commission || 0, 'XOF')}
                    </p>
                    <p className="text-xs text-gray-500">Total</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-[#D4A373]/10 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-[#D4A373]">{salesPercentage}%</p>
                <p className="text-sm text-gray-600">Ventes</p>
                <p className="text-xs text-gray-400 mt-1">
                  {formatCurrency(summary.total_sales_commission || 0, 'XOF')}
                </p>
              </div>
              <div className="bg-blue-500/10 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{rentalPercentage}%</p>
                <p className="text-sm text-gray-600">Locations</p>
                <p className="text-xs text-gray-400 mt-1">
                  {formatCurrency(summary.total_rental_commission || 0, 'XOF')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Monthly Rent Collections Table */}
      {isAdmin && (
        <Card data-testid="monthly-collections-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="w-5 h-5 text-[#1B3A4B]" />
              Bilan Mensuel des Encaissements
            </CardTitle>
            <CardDescription>Détail mois par mois des encaissements de loyers pour {selectedYear}</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mois</TableHead>
                  <TableHead className="text-center">Paiements</TableHead>
                  <TableHead className="text-right">Loyers Encaissés</TableHead>
                  <TableHead className="text-right">Ventes</TableHead>
                  <TableHead className="text-right">Commissions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyBreakdown.map((m) => {
                  const hasActivity = (m.rent_collected || 0) > 0 || (m.sales_amount || 0) > 0 || (m.total_commission || 0) > 0;
                  return (
                    <TableRow key={m.month} className={hasActivity ? '' : 'opacity-60'} data-testid={`month-row-${m.month}`}>
                      <TableCell className="font-medium capitalize">{monthNames[m.month]}</TableCell>
                      <TableCell className="text-center text-gray-600">
                        {m.rent_payments_count || 0}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-blue-600">
                        {formatCurrency(m.rent_collected || 0, 'XOF')}
                      </TableCell>
                      <TableCell className="text-right text-gray-600">
                        {(m.sales_amount || 0) > 0 ? formatCurrency(m.sales_amount, 'XOF') : '-'}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-emerald-600">
                        {formatCurrency(m.total_commission || 0, 'XOF')}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Totals row */}
                <TableRow className="bg-gray-50 font-bold border-t-2">
                  <TableCell>Total {selectedYear}</TableCell>
                  <TableCell className="text-center">
                    {monthlyBreakdown.reduce((s, m) => s + (m.rent_payments_count || 0), 0)}
                  </TableCell>
                  <TableCell className="text-right text-blue-700">
                    {formatCurrency(summary.total_rent_collected || 0, 'XOF')}
                  </TableCell>
                  <TableCell className="text-right text-gray-700">
                    {formatCurrency(summary.total_sales_amount || 0, 'XOF')}
                  </TableCell>
                  <TableCell className="text-right text-emerald-700">
                    {formatCurrency(summary.total_commission || 0, 'XOF')}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Rental Commissions by Property - Admin only */}
      {isAdmin && rentalByProperty.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-[#1B3A4B]" />
              Commissions par Bien en Location
            </CardTitle>
            <CardDescription>Détail des commissions perçues sur chaque bien</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bien</TableHead>
                  <TableHead className="text-center">Taux</TableHead>
                  <TableHead className="text-center">Paiements</TableHead>
                  <TableHead className="text-right">Loyers Encaissés</TableHead>
                  <TableHead className="text-right">Commission</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rentalByProperty
                  .sort((a, b) => b.total_commission - a.total_commission)
                  .map((prop) => (
                    <TableRow key={prop.property_id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Home className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{prop.property_title}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-[#1B3A4B] border-[#1B3A4B]">
                          {prop.commission_rate}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-gray-600">
                        {prop.payment_count}
                      </TableCell>
                      <TableCell className="text-right text-gray-600">
                        {formatCurrency(prop.total_rent_collected, 'XOF')}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-blue-600">
                        {formatCurrency(prop.total_commission, 'XOF')}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Empty state - Admin only */}
      {isAdmin && summary.total_commission === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <TrendingUp className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucune commission pour {selectedYear}
              </h3>
              <p className="text-gray-500">
                Les commissions apparaitront ici apres les transactions (ventes et locations).
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AgencyBalance;
